import {EyeOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, Form, message, Skeleton, Switch, Table} from "antd";
import {ColumnsType} from "antd/es/table";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {AiSettingsServiceContext, AppContextContext} from "../../Contexts";
import AiSettings, {OrganizationAiSettings} from "../../domain/AiSettings";
import PagedResult from "../../service/PagedResult";
import {useTableHandler} from "../../sal-ui/TableHandler";
import AiCreditUsageIndicator from "../common/AiCreditUsageIndicator";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiSettingsPage.module.css";
import {DocumentTitle} from "../DocumentTitle";
import AutorunSystemExtractorsInfo, {ORGANIZATION_SOURCE_AUTORUN_HELP} from "./AutorunSystemExtractorsInfo";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

function AiSettingsPage() {
    const appContext = useContext(AppContextContext);
    const aiSettingsService = useContext(AiSettingsServiceContext);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [settings, setSettings] = useState<AiSettings>();
    const [organizationSettings, setOrganizationSettings] = useState<PagedResult<OrganizationAiSettings>>();
    const [saving, setSaving] = useState(false);
    const title = "AI settings";
    const isSystemAdmin = appContext.user?.isSystemAdmin();
    const tableHandler = useTableHandler("name", {
        reloadFunction: reloadOrganizationSettings,
        persistentIdent: "AiSettingsOrganizationList"
    });
    const layout = {
        labelCol: {span: 8},
        wrapperCol: {span: 16},
    };
    const tailLayout = {
        wrapperCol: {offset: 8, span: 16},
    };

    const columns: ColumnsType<OrganizationAiSettings> = [
        {
            dataIndex: "organizationName",
            title: "Organization",
            key: "name",
            width: 280,
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            render: renderOrganization
        },
        {
            dataIndex: "aiFeatureEnabled",
            title: "AI feature",
            width: 260,
            render: (_value: boolean, item: OrganizationAiSettings) => renderAiFeature(item)
        },
        {
            title: "Settings",
            width: 460,
            render: renderSettings
        },
        {
            title: "Actions",
            width: 100,
            className: globalStyles["table-actions"],
            render: renderAction
        }
    ];

    useEffect(() => {
        if (isSystemAdmin) {
            tableHandler.reload();

            return;
        }

        aiSettingsService.get()
            .then(value => {
                setSettings(value);
                form.setFieldsValue(value);
            });
    }, [aiSettingsService, form, isSystemAdmin]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                {isSystemAdmin ? renderOrganizationSettingsList() : renderOwnOrganizationSettings()}
            </>
        </DocumentTitle>
    );

    function renderOrganizationSettingsList() {
        return (
            <>
                <div className={globalStyles["common__top-button-bar"]}>
                    <Button className="btn-seamless" icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
                </div>

                <Table
                    className={styles["organization-settings-table"]}
                    showSorterTooltip={false}
                    loading={tableHandler.loading}
                    dataSource={organizationSettings?.data}
                    size="middle"
                    onChange={tableHandler.onTableChange}
                    pagination={tableHandler.pagination}
                    onRow={onRow}
                    rowKey="organizationId"
                    columns={columns}
                />
            </>
        );
    }

    function renderOwnOrganizationSettings() {
        return (
            <Skeleton loading={!settings} active>
                <Form
                    {...layout}
                    form={form}
                    className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]} ${styles["settings-form"]}`}
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="autoRunSystemAiExtractorsOnOrganizationSources"
                        label="Add sys. attributes to org. sources"
                        valuePropName="checked"
                        extra={ORGANIZATION_SOURCE_AUTORUN_HELP}
                    >
                        <Switch/>
                    </Form.Item>

                    <Form.Item {...tailLayout}>
                        <AutorunSystemExtractorsInfo
                            extractors={settings?.autorunSystemExtractors}
                        />
                    </Form.Item>

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            Save
                        </Button>
                    </Form.Item>
                </Form>
            </Skeleton>
        );
    }

    function renderOrganization(value: string, item: OrganizationAiSettings) {
        return <Link to={`/organizations/${item.organizationId}`}>{value}</Link>;
    }

    function renderAiFeature(item: OrganizationAiSettings) {
        if (!item.aiFeatureEnabled) {
            return <i>disabled</i>;
        }

        if (!item.aiTier) {
            return <i>enabled, no tier</i>;
        }

        return (
            <div className={styles["ai-feature-cell"]}>
                <Link to={`/definitions/ai-tiers/${item.aiTier.id}`}>{item.aiTier.name}</Link>
                <AiCreditUsageIndicator used={item.creditsUsedThisMonth ?? 0} limit={item.aiTier.maxCreditsPerMonth ?? 0}/>
            </div>
        );
    }

    function renderSettings(_value: unknown, item: OrganizationAiSettings) {
        return (
            <div>
                Add sys. attributes to org. sources:{" "}
                <i>{formatBoolean(item.settings?.autoRunSystemAiExtractorsOnOrganizationSources)}</i>
            </div>
        );
    }

    function formatBoolean(value?: boolean) {
        return value ? "enabled" : "disabled";
    }

    function renderAction(_value: unknown, item: OrganizationAiSettings) {
        return (
            <Button
                icon={<EyeOutlined/>}
                className={"ant-btn-icon-only"}
                title={"Open AI settings"}
                onClick={() => navigate(`/ai-settings/organizations/${item.organizationId}`)}
            />
        );
    }

    function onFinish(values: AiSettings) {
        setSaving(true);

        aiSettingsService.update(values)
            .then(() => {
                message.success("AI settings updated.");
                setSettings(new AiSettings(
                    values.autoRunSystemAiExtractorsOnOrganizationSources,
                    settings?.autorunSystemExtractors
                ));

                if (appContext.user?.organization) {
                    appContext.user.organization.autoRunSystemAiExtractorsOnOrganizationSources =
                        values.autoRunSystemAiExtractorsOnOrganizationSources;
                }
            })
            .finally(() => setSaving(false));
    }

    function reloadOrganizationSettings() {
        return aiSettingsService.getOrganizations(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setOrganizationSettings(value);
        });
    }

    function onRow(item: OrganizationAiSettings) {
        return {onDoubleClick: () => navigate(`/ai-settings/organizations/${item.organizationId}`)};
    }
}

export default AiSettingsPage;
